import { type KeyboardEvent, useEffect, useState } from 'react';

/**
 * A dual-thumb price range slider with a filled "progress" track and editable
 * from/to fields. Themed from the tenant's brand tokens. The slider mechanics
 * are forced LTR (prices read left→low, right→high) even inside an RTL store.
 *
 * It only fires `onCommit` on release (mouse up / touch end / key up) so a drag
 * doesn't hammer the catalog API on every intermediate value. The two number
 * fields are the "custom" escape hatch — type an exact price and press Enter (or
 * tab out) to commit a value the drag grid can't land on; clear one to drop that
 * end of the filter entirely.
 */
const THUMB_CSS = `
.price-range__input{-webkit-appearance:none;appearance:none;background:transparent;pointer-events:none;outline:none;}
.price-range__input::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;pointer-events:auto;height:18px;width:18px;border-radius:9999px;background:var(--color-brand);border:2px solid var(--color-bg,#fff);box-shadow:0 1px 3px rgba(0,0,0,.35);cursor:pointer;transition:transform .1s ease;}
.price-range__input::-webkit-slider-thumb:active{transform:scale(1.15);}
.price-range__input::-moz-range-thumb{pointer-events:auto;height:18px;width:18px;border:2px solid var(--color-bg,#fff);border-radius:9999px;background:var(--color-brand);box-shadow:0 1px 3px rgba(0,0,0,.35);cursor:pointer;}
.price-range__input::-moz-range-track{background:transparent;}
.price-range__input:focus-visible::-webkit-slider-thumb{box-shadow:0 0 0 4px var(--color-brand-20,rgba(0,0,0,.2));}
`;

/** Plain grouped digits for the editable fields — never localised, so an Arabic
 *  store still types into `2,450,000` rather than `٢٬٤٥٠٬٠٠٠`. */
const GROUP = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/**
 * Accepts what a shopper actually types: grouping commas/spaces/dots, a currency
 * code, and Arabic-Indic digits from an Arabic keyboard. Three outcomes, kept
 * distinct on purpose — a blank field means "drop this end of the filter", but a
 * typo must NOT (silently clearing a ceiling the shopper set is worse than
 * ignoring the keystroke), so garbage reverts instead.
 */
function parseAmount(raw: string, allowDecimals: boolean): number | 'empty' | null {
  if (raw.trim() === '') return 'empty';
  const ascii = raw
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.')
    .replace(/٬/g, '');
  let cleaned = ascii.replace(/[^\d.]/g, '');
  if (!/\d/.test(cleaned)) return null;
  // A dot can't be a decimal point in a whole-dinar currency, and never when
  // there's more than one — both mean thousands grouping ("1.200.000").
  if (!allowDecimals || (cleaned.match(/\./g) ?? []).length > 1) cleaned = cleaned.replace(/\./g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

/**
 * A round drag increment: ~3,000 stops across the rail, snapped to the 1/2/5
 * ladder so every reachable value reads as a price a shopper would say out loud.
 * A multi-million IQD catalogue therefore moves in 1,000s, not 10,000s, while a
 * small range still gets a sane increment. `unit` floors it at the currency's
 * smallest sane increment.
 */
function niceStep(span: number, unit: number): number {
  const raw = Math.max(span, unit) / 3000;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const frac = raw / pow;
  const mult = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return Math.max(mult * pow, unit);
}

export function PriceRangeSlider({
  min,
  max,
  lo,
  hi,
  onCommit,
  format,
  fromLabel,
  toLabel,
  currency,
  step: stepProp,
}: {
  min: number;
  max: number;
  lo: number;
  hi: number;
  onCommit: (lo: number, hi: number) => void;
  format: (v: number) => string;
  fromLabel: string;
  toLabel: string;
  /** Shown as a muted suffix inside each field so the number stays typeable. */
  currency?: string;
  /** Override the derived drag increment. */
  step?: number;
}): JSX.Element {
  // Live drag state — committed (filter fires) only on release.
  const [live, setLive] = useState<[number, number]>([lo, hi]);
  // While a field is being typed in it owns its own text; null ⇒ mirror `live`.
  const [draftLo, setDraftLo] = useState<string | null>(null);
  const [draftHi, setDraftHi] = useState<string | null>(null);
  useEffect(() => { setLive([lo, hi]); }, [lo, hi]);

  const rawSpan = Math.max(max - min, 1);
  // Whole-number currencies (IQD) never want fractional stops; decimal ones stop at a cent.
  const unit = Number.isInteger(min) && Number.isInteger(max) ? 1 : 0.01;
  const step = stepProp && stepProp > 0 ? stepProp : niceStep(rawSpan, unit);
  // Snap the rail itself to step multiples so every dragged value is round.
  const railMin = Math.floor(min / step) * step;
  const railMax = Math.ceil(max / step) * step;
  const span = Math.max(railMax - railMin, 1);
  const [a, b] = live;
  const pctLo = ((a - railMin) / span) * 100;
  const pctHi = ((b - railMin) / span) * 100;
  const commit = (): void => onCommit(live[0], live[1]);
  // When the thumbs converge near the top end, keep the low thumb grabbable.
  const loOnTop = a > railMin + span * 0.5;
  // A hand-typed price can sit between two stops; the native thumb would snap
  // anyway, so feed it the snapped value and let the fill bar show the truth.
  const snap = (v: number): number => clamp(railMin + Math.round((v - railMin) / step) * step, railMin, railMax);

  // Empty field ⇒ that end of the filter is dropped (commits the bound, which
  // the caller reads as "no min/max"). Unparseable ⇒ revert, same as Escape.
  // Otherwise clamp into the rail and keep from ≤ to.
  const applyLo = (text: string): void => {
    const parsed = parseAmount(text, unit < 1);
    setDraftLo(null);
    if (parsed === null) return;
    const next = parsed === 'empty' ? railMin : clamp(parsed, railMin, b);
    setLive([next, b]);
    onCommit(next, b);
  };
  const applyHi = (text: string): void => {
    const parsed = parseAmount(text, unit < 1);
    setDraftHi(null);
    if (parsed === null) return;
    const next = parsed === 'empty' ? railMax : clamp(parsed, a, railMax);
    setLive([a, next]);
    onCommit(a, next);
  };

  // Both pills share the row evenly and may shrink; on a phone the From/To words
  // and the currency suffix drop out so the digits themselves never get clipped
  // (the dash still reads the pair as a range, and the aria-labels stay).
  const field = 'inline-flex min-w-0 flex-1 items-center gap-1.5 rounded-full border border-border bg-bg-subtle px-2.5 py-1 transition focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20';
  const box = 'w-full min-w-0 bg-transparent text-end font-mono text-xs font-semibold text-fg outline-none';
  const affix = 'hidden whitespace-nowrap text-[11px] font-medium text-fg-muted sm:inline';

  const onFieldKey = (apply: (text: string) => void, revert: () => void) =>
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') { e.preventDefault(); apply(e.currentTarget.value); }
      else if (e.key === 'Escape') { e.preventDefault(); revert(); }
    };

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col gap-2 sm:min-w-[17rem]">
      <style>{THUMB_CSS}</style>
      <div className="flex items-center justify-between gap-1.5 sm:gap-2">
        <label className={field} title={format(a)}>
          <span className={affix}>{fromLabel}</span>
          <input
            dir="ltr"
            inputMode="decimal"
            className={box}
            value={draftLo ?? GROUP.format(a)}
            onChange={(e) => setDraftLo(e.target.value)}
            onBlur={(e) => { if (draftLo !== null) applyLo(e.target.value); }}
            onKeyDown={onFieldKey(applyLo, () => setDraftLo(null))}
            aria-label={fromLabel}
          />
          {currency && <span className={affix}>{currency}</span>}
        </label>
        <span className="shrink-0 text-xs text-fg-muted" aria-hidden>–</span>
        <label className={field} title={format(b)}>
          <span className={affix}>{toLabel}</span>
          <input
            dir="ltr"
            inputMode="decimal"
            className={box}
            value={draftHi ?? GROUP.format(b)}
            onChange={(e) => setDraftHi(e.target.value)}
            onBlur={(e) => { if (draftHi !== null) applyHi(e.target.value); }}
            onKeyDown={onFieldKey(applyHi, () => setDraftHi(null))}
            aria-label={toLabel}
          />
          {currency && <span className={affix}>{currency}</span>}
        </label>
      </div>
      <div className="relative h-5" dir="ltr">
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-border" />
        <div className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-brand" style={{ left: `${pctLo}%`, right: `${100 - pctHi}%` }} />
        <input
          type="range"
          className="price-range__input absolute inset-x-0 top-0 h-5 w-full"
          style={{ zIndex: loOnTop ? 4 : 3 }}
          min={railMin}
          max={railMax}
          step={step}
          value={snap(a)}
          onChange={(e) => { const v = Math.min(Number(e.target.value), b); setLive([v, b]); setDraftLo(null); }}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          aria-label={fromLabel}
          aria-valuetext={format(a)}
        />
        <input
          type="range"
          className="price-range__input absolute inset-x-0 top-0 h-5 w-full"
          style={{ zIndex: loOnTop ? 3 : 4 }}
          min={railMin}
          max={railMax}
          step={step}
          value={snap(b)}
          onChange={(e) => { const v = Math.max(Number(e.target.value), a); setLive([a, v]); setDraftHi(null); }}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          aria-label={toLabel}
          aria-valuetext={format(b)}
        />
      </div>
    </div>
  );
}
